import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import dayjs from 'dayjs';
import { SilentError } from 'init/silent-error';
import { useEffect } from 'react';
import { isDefined } from 'remeda';
import {
  type ApiError,
  addResourceNameToQueryResult,
  handleZodError,
  type QueryOptions,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { z } from 'zod/v4';
import { StepTypeSchema } from './step-type';

const URL = '/templates';

export const TemplateTypeSchema = z.enum(['recovery', 'payment_plan']);
export type TemplateType = z.infer<typeof TemplateTypeSchema>;

const UsedByResourceSchema = z.object({
  id: z.coerce.string(),
  name: z.string(),
});

export type UsedByResource = z.infer<typeof UsedByResourceSchema>;

const LightTemplateSchema = z.object({
  id: z.coerce.string(),
  name: z.string(),
  stepType: StepTypeSchema,
  planType: z.enum(['recovery', 'payment']),
  includePDF: z.boolean(),
  includeUbl: z.boolean(),
  updatedAt: z.iso.datetime().transform(dayjs),
  usedBy: z.array(UsedByResourceSchema),
  noDeletionReason: z.string().nullable(),
  channels: z.array(z.enum(['email', 'letter'])),
});

export type LightTemplate = z.infer<typeof LightTemplateSchema>;

const LoadTemplatesResponseSchema = z.object({
  data: z.array(LightTemplateSchema),
});

export type LoadTemplatesParams = {
  templateType: TemplateType;
};

async function loadTemplatesQueryFn(
  axiosInstance: Promise<AxiosInstance>,
  params: LoadTemplatesParams,
) {
  const instance = await axiosInstance;
  const { data } = await instance.get(URL, { params });

  const parseResult = LoadTemplatesResponseSchema.safeParse(data);
  if (!parseResult.success) {
    handleZodError(parseResult, 'templates');
    throw new SilentError();
  }

  return parseResult.data.data;
}

const emptyTemplates: LightTemplate[] = [];

export function useLoadTemplates(
  params: LoadTemplatesParams,
  options: QueryOptions<LightTemplate[]> = {},
) {
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<LightTemplate[], ApiError>({
    queryKey: ['templates', params],
    queryFn: () => loadTemplatesQueryFn(axiosInstance, params),
    placeholderData: previousData => {
      if (isDefined(previousData)) return previousData;
      return emptyTemplates;
    },
    ...options,
  });

  return addResourceNameToQueryResult<LightTemplate[], ApiError, 'templates', 'PreventUndefined'>(
    'templates',
    queryResult,
    emptyTemplates,
  );
}

export function usePrefetchTemplates(params: LoadTemplatesParams) {
  const queryClient = useQueryClient();
  const axiosInstance = useAxiosInstance();

  // biome-ignore lint/correctness/useExhaustiveDependencies: prefetch when the template type changes; queryClient and the axios instance are read fresh
  useEffect(() => {
    queryClient.prefetchQuery({
      queryKey: ['templates', params],
      queryFn: () => loadTemplatesQueryFn(axiosInstance, params),
    });
  }, [params.templateType]);
}
